import { styled } from "@mui/material/styles";
import type AHDate from "_shared/dates/date";
import { KeyboardKey } from "_shared/_utilities/keyboard-keys";
import BubbleTimestamp from "messaging/correspondence/_views/bubble-timestamp";
import { BubbleTime } from "messaging/correspondence/_views/message-bubble";
import { revealHoverActions } from "messaging/correspondence/_views/message-hover-actions";
import { unreadRule } from "messaging/correspondence/_views/read-state-controls";
import React from "react";

/**
 * What the platform said in a conversation — "The work agreement was fully accepted…" — drawn the same
 * way in both Correspondence panes: its time over it, centered, in the bubbles' own form and gray, and
 * the text in a quiet box, smaller and grayer than what people said, so it reads as an announcement
 * rather than one more message.
 *
 * The two panes each drew their own. The chat gave the date as "09/11/26 02:41 PM" in black over bubbles
 * saying "Sep 11" in gray; the proposal pane put the time under the box, at its left.
 */
const Container = styled("div")(({ theme }) => ({
  "@container (min-width: 800px)": {
    width: "75%",
  },
  "@container (min-width: 0px)": {
    width: "100%",
  },
  boxSizing: "border-box",
  margin: theme.spacing(0, "auto", 2, "auto"),
  width: "80%",
  ...revealHoverActions,
}));

/**
 * The time, the status and the read-state dot, centered over the box. Lined up on their centers: on a
 * baseline, the 28px button sat below the time it belongs to.
 */
const Info = styled("div")(({ theme }) => ({
  alignItems: "center",
  columnGap: theme.spacing(0.75),
  display: "flex",
  justifyContent: "center",
  marginBottom: theme.spacing(0.5),
  minHeight: "28px",
}));

/**
 * The box. An unread notice carries the rule down its edge (#5828) and its text one step stronger
 * (#5127), so its two states differ at a glance down the whole conversation, not only by the dot.
 */
const Content = styled("div", {
  shouldForwardProp: (prop) => prop !== "unread",
})<{ unread: boolean }>(({ theme, unread }) => ({
  backgroundColor: theme.palette.grey[100],
  borderLeft: unreadRule(theme, unread),
  borderRadius: theme.spacing(1),
  color: unread ? theme.palette.text.primary : theme.palette.text.secondary,
  cursor: unread ? "pointer" : undefined,
  display: "block",
  fontFamily: "Roboto, sans-serif",
  fontSize: "0.8125rem",
  fontWeight: unread ? 500 : undefined,
  lineHeight: 1.5,
  overflowWrap: "anywhere",
  padding: theme.spacing(1, 1.5),
  textAlign: "center",
  whiteSpace: "normal",
  "& p": {
    margin: 0,
  },
  "& p + p": {
    marginTop: theme.spacing(1),
  },
}));

type SystemNoticeProps = {
  published?: AHDate;
  /** Edited, History: what has happened to the notice, beside its time. */
  status?: React.ReactNode;
  /** The read-state button, after the time. */
  readState?: React.ReactNode;
  /** The reader has not read it: the box carries the rule, and a press or Enter marks it read. */
  unread?: boolean;
  onMarkRead?: () => void;
  children: React.ReactNode;
} & Omit<React.HTMLAttributes<HTMLDivElement>, "children">;

export const SystemNotice = React.forwardRef<HTMLDivElement, SystemNoticeProps>(function SystemNotice(props, ref) {
  const { published, status, readState, unread, onMarkRead, children, ...rest } = props;
  const isUnread = Boolean(unread);
  // Named for what a press does, as an unread bubble is. Named by its own text, it answered any button
  // lookup by a word the notice happened to contain: "Accept" found "…to accept the handover…" too.
  const pressable =
    isUnread && onMarkRead
      ? {
          role: "button",
          tabIndex: 0,
          "aria-label": "Unread notice. Press to mark it read.",
          onClick: onMarkRead,
          onKeyDown: (event: React.KeyboardEvent) => {
            if (event.key !== KeyboardKey.Enter && event.key !== KeyboardKey.Space) return;
            event.preventDefault();
            onMarkRead();
          },
        }
      : {};

  return (
    <Container ref={ref} {...rest}>
      <Info data-testid="system-message-info">
        {status}
        <BubbleTime>{published && <BubbleTimestamp date={published} />}</BubbleTime>
        {readState}
      </Info>
      <Content
        unread={isUnread}
        data-testid="system-message-content"
        data-unread={isUnread ? "true" : undefined}
        {...pressable}
      >
        {children}
      </Content>
    </Container>
  );
});
